import { Editor, type Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { ResizableImage } from './tiptap-resizable-image';
// EAA-1736: audio now has a NodeView so it supports the shared media layout.
import { AudioWithView } from './tiptap-audio-view';
import { EmbedWithView } from './tiptap-embed-view';
import { ImagePasteDrop } from './tiptap-image-paste-drop';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

export type TiptapJSON = {
  type?: string;
  content?: TiptapJSON[];
  [key: string]: unknown;
};

const DEFAULT_PLACEHOLDER = 'Start typing or press Tab for more options...';

export function getRichTextExtensions(
  placeholder: string = DEFAULT_PLACEHOLDER
): Extensions {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4] },
      link: false,
      underline: false,
      horizontalRule: false,
    }),
    Underline,
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    Subscript,
    Superscript,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
    }),
    // EAA-1736: media are inline-level so they can flow within a line of text
    // (the "inline" layout). Block/wrap/overlay layouts still work via CSS on
    // the inline node. Legacy block-positioned media are migrated on load by
    // normalizeMediaNodesToInline().
    ResizableImage.configure({
      inline: true,
      allowBase64: true,
      HTMLAttributes: {},
    }),
    AudioWithView,
    EmbedWithView,
    // EAA-1584: column resizing. cellMinWidth stops columns collapsing into
    // 25px slivers (the prosemirror default) when a neighbour is widened, and
    // lastColumnResizable:false disables the outer-right handle so users cannot
    // balloon the whole table past the editor width — the table stays bounded
    // to width:100% and inner drags just redistribute space between columns.
    Table.configure({
      resizable: true,
      cellMinWidth: 60,
      lastColumnResizable: false,
      HTMLAttributes: { class: 'tiptap-table border-collapse border border-border' },
    }),
    TableRow.configure({
      HTMLAttributes: { class: 'tiptap-table-row' },
    }),
    TableHeader.configure({
      HTMLAttributes: { class: 'tiptap-table-header' },
    }),
    TableCell.configure({
      HTMLAttributes: { class: 'tiptap-table-cell' },
    }),
    Placeholder.configure({ placeholder }),
    Markdown,
    ImagePasteDrop,
  ];
}

/** Convert Markdown string to Tiptap JSON */
export function markdownToTiptap(markdown: string): TiptapJSON {
  if (!markdown || typeof markdown !== 'string' || !markdown.trim()) {
    return createEmptyDocument();
  }
  try {
    const extensions = getRichTextExtensions();
    const editor = new Editor({
      element: null,
      extensions,
      content: createEmptyDocument(),
    });
    const json = (editor as unknown as { markdown: { parse: (md: string) => TiptapJSON } }).markdown.parse(markdown.trim());
    editor.destroy();
    return json ?? createEmptyDocument();
  } catch {
    return createParagraphContent(markdown);
  }
}

/** Convert Tiptap JSON to Markdown string */
export function tiptapToMarkdown(doc: TiptapJSON): string {
  if (!doc || typeof doc !== 'object' || doc.type !== 'doc') {
    return '';
  }
  try {
    const extensions = getRichTextExtensions();
    const editor = new Editor({
      element: null,
      extensions,
      // EAA-1736: migrate legacy block media so the inline schema accepts it.
      content: normalizeMediaNodesToInline(doc),
    });
    const md = (editor.storage as { markdown?: { getMarkdown?: () => string } }).markdown?.getMarkdown?.() ?? '';
    editor.destroy();
    return md.trim();
  } catch {
    return tiptapJsonToPlainText(doc);
  }
}

/** Lightweight plain-text extraction from Tiptap JSON (no editor needed). */
export function tiptapJsonToPlainText(node: TiptapJSON): string {
  if (!node || typeof node !== 'object') return '';
  if (node.type === 'text') return (node.text as string) ?? '';
  const children = node.content;
  if (!Array.isArray(children)) return '';
  const parts: string[] = [];
  for (const child of children) {
    const text = tiptapJsonToPlainText(child);
    if (text) parts.push(text);
  }
  const isBlock = node.type === 'paragraph' || node.type === 'heading' || node.type === 'blockquote'
    || node.type === 'listItem' || node.type === 'doc';
  return isBlock ? parts.join('\n') : parts.join('');
}

export function createEmptyDocument(): TiptapJSON {
  return {
    type: 'doc',
    content: [{ type: 'paragraph' }],
  };
}

export function createParagraphContent(text: string): TiptapJSON {
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: text ? [{ type: 'text', text }] : undefined,
      },
    ],
  };
}

// EAA-1736: media nodes (image/audio/embed) are now inline-level, so they must
// live inside an inline-holding parent (paragraph/heading). Legacy documents
// stored them as block-level children of doc/list/quote/table cells. This wraps
// any such block-positioned media in a paragraph so old content stays valid
// (and visible) under the new inline schema. Idempotent: re-running is a no-op.
const INLINE_MEDIA_TYPES = new Set(['image', 'audio', 'embed']);
const BLOCK_MEDIA_CONTAINERS = new Set([
  'doc',
  'listItem',
  'blockquote',
  'tableCell',
  'tableHeader',
]);

export function normalizeMediaNodesToInline(doc: TiptapJSON): TiptapJSON {
  const transform = (node: TiptapJSON): TiptapJSON => {
    if (!Array.isArray(node.content)) return node;
    const nextContent = node.content.map(transform);
    if (BLOCK_MEDIA_CONTAINERS.has(node.type ?? '')) {
      for (let i = 0; i < nextContent.length; i++) {
        const child = nextContent[i];
        if (INLINE_MEDIA_TYPES.has(child.type ?? '')) {
          nextContent[i] = { type: 'paragraph', content: [child] };
        }
      }
    }
    return { ...node, content: nextContent };
  };
  return transform(doc);
}

export function isTiptapDocument(value: unknown): value is TiptapJSON {
  return (
    value != null &&
    typeof value === 'object' &&
    'type' in value &&
    (value as TiptapJSON).type === 'doc'
  );
}
